import { EventSystem, IEventSystem } from "@dhx/ts-common/events";
import { View } from "@dhx/ts-common/view";
import { Layout, ILayoutConfig, ICellConfig } from "@dhx/ts-layout";
import { CalendarEvents } from "@dhx/ts-calendar";
import { extend, uid, isDefined } from "@dhx/ts-common/core";
import { IAnyObj } from "@dhx/ts-common/types";
import { TimepickerEvents } from "@dhx/ts-timepicker";
import { ColorpickerEvents } from "@dhx/ts-colorpicker";
import { DateInput } from "./elements/dateInput";
import { Button, ButtonEvents } from "./elements/button";
import { Checkbox } from "./elements/checkbox";
import { Input } from "./elements/input";
import { RadioGroup } from "./elements/radioGroup";
import { Select } from "./elements/select";
import { Textarea } from "./elements/textarea";
import { Text } from "./elements/textinput";
import { Combo } from "./elements/combo";
import { SliderForm } from "./elements/sliderform";
import { isBlock } from "./helper";
import { SimpleVault } from "./elements/simplevault";
import { TimeInput } from "./elements/timeInput";
import { ColorpickerInput } from "./elements/colorpicker";
import { FormEvents, FormItemType, IBlock, IFormItem, IFormItemWithoutBlock,
		IFormEventHandlersMap, IForm, ClearMethod, BaseElementEvent, IFormLayout } from "./types";

export class Form extends View implements IForm {
	public config: IBlock;
	public events: IEventSystem<FormEvents, IFormEventHandlersMap>;
	public layout: Layout;

	private _attachments: { [id: string]: any };
	private _state: { [key: string]: any };
	private container: HTMLElement | string;

	constructor(container: HTMLElement | string, config: IBlock) {
		super(null, extend({
			labelWidth: "auto",
			inputType: "text"
		}, config));
		this.events = new EventSystem<FormEvents>(this);
		this._state = {};
		this.container = container;
		this._initUI(container);
	}
	send(url: string, method: string = "POST", asFormData?: boolean): Promise<any> | void {
		if (this.events.fire(FormEvents.beforeSend)) {
			return new Promise((resolve, reject) => {
				const xhr = new XMLHttpRequest();

				xhr.onload = () => {
					if (xhr.status === 200) {
						resolve(xhr.response || xhr.responseText);
					} else {
						reject({
							status: xhr.status,
							statusText: xhr.statusText
						});
					}
				};
				xhr.onloadend = () => {
					if (xhr.readyState === XMLHttpRequest.DONE && xhr.status === 200) {
						this.events.fire(FormEvents.afterSend);
					}
				};
				xhr.onerror = () => {
					reject({
						status: xhr.status,
						statusText: xhr.statusText
					});
				};
				if (method === "GET") {
					url += `?` + encodeURIComponent(JSON.stringify(this.getValue()));
				}

				xhr.open(method, url);

				if (!asFormData) {
					xhr.setRequestHeader("Content-Type", "application/json");
				}

				switch (method) {
					case "POST":
					case "DELETE":
					case "PUT":
						xhr.send(asFormData ? this.getValue(true) as FormData : JSON.stringify(this.getValue()));
						break;
					case "GET":
						xhr.send();
					default:
						xhr.send();
						break;
				}
			});
		}
	}
	setConfig(config: IBlock): void {
		this.unmount();
		this.config = config;
		this._initUI(this.container);
		this.paint();
	}
	clear(method?: string): void {
		switch (method) {
			case ClearMethod.value:
				this._clear();
				break;
			case ClearMethod.validation:
				this._clearValidate();
				break;
			default:
				this._clear();
				this._clearValidate();
				break;
		}
		this.paint();
	}
	setValue(obj: IAnyObj): void {
		for (const item in obj) {
			for (const key in this._attachments) {
				if (typeof this._attachments[key].setValue === "function") {
					if (this._attachments[key].config.name === item || this._attachments[key].config.id === item && !this._attachments[key].config.name) {
						this._attachments[key].setValue(obj[item]);
					}
				}
			}
		}
	}
	getValue<T extends boolean = false>(asFormData?: boolean): T extends true ? FormData : IAnyObj {
		if (asFormData) {
			const formData = new FormData();
			for (const key in this._state) {
				if (Array.isArray(this._state[key])) {
					for (const value of this._state[key]) {
						formData.append(key + "[]", value);
					}
				} else {
					formData.append(key, this._state[key]);
				}
			}
			return formData as any;
		}
		return this._state as any;
	}
	getItem(id: string): IBlock {
		for (const item in this._attachments) {
			if (item === id) {
				return this._attachments[item];
			}
		}
	}
	validate(): boolean {
		const attachments = this._attachments;
		let isValid = true;
		for (const id in attachments) {
			const component = attachments[id];
			if (component.validate && !component.validate()) {
				isValid = false;
				this.events.fire(FormEvents.validationFail, [id, component]);
			}
		}
		return isValid;
	}
	getRootView() {
		return this.layout.getRootView();
	}
	destructor() {
		this.events.clear();
		this.unmount();
	}
	private _addLayoutItem(item: IFormItemWithoutBlock) {
		item.id = item.id || uid();
		const id = item.id;
		const name = item.name || item.id;
		const { width, height, cellCss, gravity, ...config } = item;
		const cell: ICellConfig = {
			id,
			width,
			height,
			css: cellCss,
		};
		if ("gravity" in item) {
			cell.gravity = item.gravity;
		}
		switch (config.type) {
			case FormItemType.button:
				const button = this._attachments[id] = new Button(null, config);
				button.events.on(ButtonEvents.click, e => {
					e.preventDefault();
					if (config.submit) {
						if (this.validate()) {
							this.events.fire(FormEvents.buttonClick, [id, e]);
						}
					} else {
						this.events.fire(FormEvents.buttonClick, [id, e]);
					}
				});
				break;
			case FormItemType.datepicker:
				const dateInput = this._attachments[id] = new DateInput(null, config);
				this._state[name] = dateInput.getValue();
				dateInput.calendar.events.on(CalendarEvents.change, () => {
					const value = dateInput.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				dateInput.events.on(BaseElementEvent.configUpdate, newConfig => {
					let value = dateInput.getValue();
					this._state[name] = value;
					this.layout.getCell(dateInput.config.id).config = this._checkLayoutConfig(newConfig, {});
					dateInput.calendar.events.on(CalendarEvents.change, () => {
						value = dateInput.getValue();
						this._state[name] = value;
						this.events.fire(FormEvents.change, [name, value]);
					});
				});
				break;
			case FormItemType.checkbox:
				const checkbox = this._attachments[id] = new Checkbox(null, config);
				this._state[name] = checkbox.getValue();
				checkbox.events.on(BaseElementEvent.change, () => {
					const value = checkbox.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				checkbox.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = checkbox.getValue();
					this._state[name] = value;
					this.layout.getCell(checkbox.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.combo:
				const combo = this._attachments[id] = new Combo(config);
				this._state[name] = combo.getValue();
				combo.events.on(BaseElementEvent.change, selected => {
					const value = combo.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, selected]);
				});
				combo.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = combo.getValue();
					this._state[name] = value;
					this.layout.getCell(combo.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.input:
				const input = this._attachments[id] = new Input(null, config);
				this._state[name] = input.getValue();
				input.events.on(BaseElementEvent.change, () => {
					const value = input.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				input.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = input.getValue();
					this._state[name] = value;
					this.layout.getCell(input.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.radioGroup:
				const radioGroup = this._attachments[id] = new RadioGroup(null, config);
				this._state[name] = radioGroup.getValue();
				radioGroup.events.on(BaseElementEvent.change, () => {
					const value = radioGroup.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				radioGroup.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = radioGroup.getValue();
					this._state[name] = value;
					this.layout.getCell(radioGroup.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.select:
				const select = this._attachments[id] = new Select(config);
				this._state[name] = select.getValue();
				select.events.on(BaseElementEvent.change, () => {
					const value = select.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				select.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = select.getValue();
					this._state[name] = value;
					this.layout.getCell(select.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.simpleVault:
				config.$vaultHeight = height;
				const simpleVault = this._attachments[id] = new SimpleVault(null, config);
				this._state[name] = simpleVault.getValue();
				simpleVault.events.on(BaseElementEvent.change, () => {
					const value = simpleVault.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				simpleVault.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = simpleVault.getValue();
					this._state[name] = value;
					this.layout.getCell(simpleVault.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.slider:
				const slider = this._attachments[id] = new SliderForm(null, config);
				this._state[name] = slider.getValue();
				slider.events.on(BaseElementEvent.change, () => {
					const value = slider.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				slider.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = slider.getValue();
					this._state[name] = value;
					this.layout.getCell(slider.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.textarea:
				const textarea = this._attachments[id] = new Textarea(null, config);
				this._state[name] = textarea.getValue();
				textarea.events.on(BaseElementEvent.change, () => {
					const value = textarea.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				textarea.events.on(BaseElementEvent.configUpdate, newConfig => {
					const value = textarea.getValue();
					this._state[name] = value;
					this.layout.getCell(textarea.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.text:
				const text = this._attachments[id] = new Text(null, config);
				text.events.on(BaseElementEvent.configUpdate, newConfig => {
					this.layout.getCell(text.config.id).config = this._checkLayoutConfig(newConfig, {});
				});
				break;
			case FormItemType.timepicker:
				const timeInput = this._attachments[id] = new TimeInput(null, config);
				this._state[name] = timeInput.getValue();
				if (timeInput.config.actions) {
					timeInput.timepicker.events.on(TimepickerEvents.save, () => {
						const value = timeInput.getValue();
						this._state[name] = value;
						this.events.fire(FormEvents.change, [name, value]);
					});
				} else {
					timeInput.timepicker.events.on(TimepickerEvents.change, () => {
						const value = timeInput.getValue();
						this._state[name] = value;
						this.events.fire(FormEvents.change, [name, value]);
					});
				}
				timeInput.events.on(BaseElementEvent.configUpdate, newConfig => {
					let value = timeInput.getValue();
					this._state[name] = value;
					this.layout.getCell(timeInput.config.id).config = this._checkLayoutConfig(newConfig, {});
					if (timeInput.config.actions) {
						timeInput.timepicker.events.on(TimepickerEvents.save, () => {
							value = timeInput.getValue();
							this._state[name] = value;
							this.events.fire(FormEvents.change, [name, value]);
						});
					} else {
						timeInput.timepicker.events.on(TimepickerEvents.change, () => {
							value = timeInput.getValue();
							this._state[name] = value;
							this.events.fire(FormEvents.change, [name, value]);
						});
					}
				});
				break;
			case FormItemType.colorpicker:
				const colorpickerInput = this._attachments[id] = new ColorpickerInput(null, config);
				this._state[name] = colorpickerInput.getValue();
				colorpickerInput.colorpicker.events.on(ColorpickerEvents.change, () => {
					const value = colorpickerInput.getValue();
					this._state[name] = value;
					this.events.fire(FormEvents.change, [name, value]);
				});
				colorpickerInput.events.on(BaseElementEvent.configUpdate, newConfig => {
					let value = colorpickerInput.getValue();
					this._state[name] = value;
					this.layout.getCell(colorpickerInput.config.id).config = this._checkLayoutConfig(newConfig, {});
					colorpickerInput.colorpicker.events.on(ColorpickerEvents.change, () => {
						value = colorpickerInput.getValue();
						this._state[name] = value;
						this.events.fire(FormEvents.change, [name, value]);
					});
				});
				break;
		}
		return cell;
	}
	private _addLayoutItems(items: IFormItem[], group?: FormItemType, groupName?: string): any[] {
		return items.map(item => {
			item.type = item.type || group;
			if (isBlock(item)) {
				const layoutConfig: ILayoutConfig = {};
				this._createLayoutConfig(item, layoutConfig);
				return layoutConfig;
			} else {
				item.name = item.name || groupName;
			}
			return this._addLayoutItem(item);
		});
	}
	private _checkLayoutConfig(config: IFormLayout, layoutConfig: ILayoutConfig) {
		if (isDefined(config.cellCss)) {
			layoutConfig.css = config.cellCss;
		}
		if (isDefined(config.title)) {
			layoutConfig.header = config.title;
		}
		if (isDefined(config.padding)) {
			layoutConfig.padding = config.padding;
		}
		if (isDefined(config.gravity)) {
			layoutConfig.gravity = config.gravity;
		}
		if (isDefined(config.width)) {
			layoutConfig.width = config.width;
		}
		if (isDefined(config.height)) {
			layoutConfig.height = config.height;
		}
		if (isDefined(config.align)) {
			layoutConfig.align = config.align;
		}
		return layoutConfig;
	}
	private _createLayoutConfig(config: IBlock, layoutConfig: ILayoutConfig) {
		layoutConfig = this._checkLayoutConfig(config, layoutConfig);

		if (isDefined(config.rows)) {
			layoutConfig.rows = this._addLayoutItems(config.rows, config.group, config.groupName);
		} else if (isDefined(config.cols)) {
			layoutConfig.cols = this._addLayoutItems(config.cols, config.group, config.groupName);
		}
	}
	private _initUI(container: HTMLElement | string) {
		const attachments = this._attachments = {};
		const layoutConfig: ILayoutConfig = { padding: "8px" };
		this._createLayoutConfig(this.config, layoutConfig);
		const layout = this.layout = new Layout(container, layoutConfig);
		for (const id in attachments) {
			layout.getCell(id).attach(attachments[id]);
		}
	}
	private _clear(): void {
		this._state = {};
		for (const key in this._attachments) {
			const name = this._attachments[key].config.name;
			if (typeof this._attachments[key].clear === "function") {
				this._attachments[key].clear();
				name ? this._state[name] = this._attachments[key].getValue() : this._state[key] = this._attachments[key].getValue();
			}
		}
	}
	private _clearValidate(): void {
		for (const key in this._attachments) {
			if (typeof this._attachments[key].clearValidate === "function") {
				this._attachments[key].clearValidate();
			}
		}
	}
}